import { Injectable, inject } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { MaintenanceStatusService } from './maintenance-status.service';
import { EventManagerService } from './event-manager.service';
import { UserSessionService } from './user-session.service';
import { StorageService } from './storage.service';
import { defaultConfig } from 'src/app/app.config';

const { sessionEnded } = defaultConfig.appEvents;

const DEFAULT_TITLE = 'Mantenimiento programado';
const DEFAULT_DESCRIPTION =
  'Por motivo de cierre contable la aplicación no se encuentra disponible. Agradecemos su comprensión y lo invitamos a intentar más tarde';

const STORE_NAME = 'maintenance';
const HANDLED_WINDOW_KEY = 'sessionClosedForWindow';

/**
 * Vigila el estado de mantenimiento mientras hay una sesión activa: si el mantenimiento se
 * activa, cierra la sesión (sessionEnded) y avisa. NO notifica nada sin sesión — el chequeo
 * para el login/registro público vive en el punto de acción (home-public.component.ts), no
 * aquí, para no bombardear a un usuario anónimo con un aviso cada vez que abre la app.
 *
 * El "ya se cerró por esta ventana" se persiste en StorageService (no en memoria): el logout
 * completo (RP-Initiated Logout en Hydra) navega fuera de la SPA y de vuelta; si algo interrumpe
 * ese viaje (recarga, timeout de red) el estado en memoria se pierde y, sin esta marca, el primer
 * chequeo del reload volvería a encontrar el mantenimiento activo y mostraría el aviso otra vez
 * justo después de haber cerrado la sesión por él.
 */
@Injectable({
  providedIn: 'root',
})
export class MaintenanceGuardService {
  private _maintenanceStatus = inject(MaintenanceStatusService);
  private _modal = inject(NzModalService);
  private _eventManager = inject(EventManagerService);
  private _userSession = inject(UserSessionService);
  private _storage = inject(StorageService);

  private static readonly POLL_INTERVAL_MS = 2 * 60 * 1000;

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.pollTimer) {
      return;
    }
    this.checkNow();
    this.pollTimer = setInterval(() => this.checkNow(), MaintenanceGuardService.POLL_INTERVAL_MS);
  }

  stop(): void {
    this.pollTimer && clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private async checkNow(): Promise<void> {
    try {
      const status = await this._maintenanceStatus.check();
      if (!status.active) {
        await this._storage.removeItem(STORE_NAME, HANDLED_WINDOW_KEY);
        return;
      }
      const hasActiveSession = !!this._userSession.getSessionData()?.sessionId;
      if (!hasActiveSession) {
        return;
      }
      const windowKey = this.windowKey(status.startTime, status.endTime);
      const handledWindow = await this._storage.getItem<string>(STORE_NAME, HANDLED_WINDOW_KEY);
      if (handledWindow === windowKey) {
        return;
      }
      await this._storage.setItem(STORE_NAME, HANDLED_WINDOW_KEY, windowKey);
      this.showMaintenanceMessage(status.title, status.description);
    } catch {
      // Nunca romper el poll por un fallo de red/almacenamiento; el siguiente ciclo reintenta.
    }
  }

  private windowKey(startTime: string | null, endTime: string | null): string {
    return `${startTime ?? ''}|${endTime ?? ''}`;
  }

  private showMaintenanceMessage(title: string | null, description: string | null): void {
    this._modal.warning({
      nzTitle: title || DEFAULT_TITLE,
      nzContent: description || DEFAULT_DESCRIPTION,
      nzOkText: 'Aceptar',
      nzClosable: false,
      nzMaskClosable: false,
      nzOnOk: () => {
        this._eventManager.next(sessionEnded.name, null);
      },
    });
  }
}
